import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel,
    Skeleton, IconButton, Tooltip
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from 'primereact/toast';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Colours match the attendance grid's status chips so the same idea reads the
// same way on both screens.
const KIND_STYLE = {
    leave: { bg: '#FFF1F0', border: '#FFCCC7', color: '#F5222D' },
    on_leave: { bg: '#FFF1F0', border: '#FFCCC7', color: '#F5222D' },
    absent: { bg: '#FFF1F0', border: '#FFCCC7', color: '#F5222D' },
    missing_punch: { bg: '#FFF1F0', border: '#FFCCC7', color: '#F5222D' },
    comp_off: { bg: '#F6FFED', border: '#B7EB8F', color: '#52C41A' },
    comp_off_earned: { bg: '#F6FFED', border: '#B7EB8F', color: '#52C41A' },
    on_duty: { bg: '#E6F4FF', border: '#91CAFF', color: '#0071BC' },
    permission: { bg: '#FFF7E6', border: '#FFD591', color: '#A85700' },
    late: { bg: '#FFF7E6', border: '#FFD591', color: '#A85700' },
    half_day: { bg: '#FFF7E6', border: '#FFD591', color: '#A85700' },
    week_off: { bg: '#FAFAFA', border: '#EDEDED', color: '#6B7280' },
    holiday: { bg: '#FAFAFA', border: '#EDEDED', color: '#6B7280' },
    present: { bg: '#FFFFFF', border: '#EDEDED', color: '#333333' },
};

const LEGEND = [
    { kind: 'present', label: 'Present' },
    { kind: 'leave', label: 'Leave' },
    { kind: 'comp_off', label: 'Comp-off' },
    { kind: 'on_duty', label: 'On-duty' },
    { kind: 'permission', label: 'Permission' },
    { kind: 'week_off', label: 'Week-off / holiday' },
];

export function StaffCalendar() {
    const selectedHub = useSelector(state => state.user.selected_hub);
    const toast = useRef(null);

    const [staffOptions, setStaffOptions] = useState([]);
    const [staffId, setStaffId] = useState('');
    const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const hubId = useMemo(() => {
        const normalize = (value) => {
            if (value === null || value === undefined) return null;
            if (Array.isArray(value)) return normalize(value[0]);
            const n = Number(value);
            return Number.isFinite(n) ? n : null;
        };
        const fromStore = normalize(selectedHub);
        if (fromStore) return fromStore;
        const stored = localStorage.getItem('selected_hub');
        if (!stored) return null;
        try {
            return normalize(JSON.parse(stored));
        } catch (e) {
            return normalize(stored);
        }
    }, [selectedHub]);

    const showToast = (severity, summary, detail) => {
        if (toast.current) toast.current.show({ severity, summary, detail, life: 3500 });
    };

    useEffect(() => {
        if (!hubId) return;
        (async () => {
            try {
                const res = await CustomAxios.get('staff/options/', { params: { hub_id: hubId } });
                setStaffOptions(res.data || []);
                if (res.data?.length && !staffId) setStaffId(res.data[0].id);
            } catch (error) {
                setStaffOptions([]);
            }
        })();
    }, [hubId]);

    useEffect(() => {
        if (!staffId) return;
        fetchCalendar();
    }, [staffId, month]);

    const fetchCalendar = async () => {
        try {
            setLoading(true);
            const res = await CustomAxios.get('staff/calendar/', {
                params: { staff_id: staffId, month },
            });
            setData(res.data);
        } catch (error) {
            showToast('error', 'Error', error?.response?.data?.error || 'Failed to load calendar');
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const shiftMonth = (delta) => setMonth(dayjs(`${month}-01`).add(delta, 'month').format('YYYY-MM'));

    // Pad the grid so day 1 lands under its real weekday.
    const leadingBlanks = useMemo(
        () => (data ? Array.from({ length: data.first_weekday }, (_, i) => i) : []),
        [data]
    );

    const balances = data?.balances?.filter(b => b.leave_category !== 'lop') || [];
    const lop = data?.balances?.find(b => b.leave_category === 'lop');

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Typography variant="h5">Calendar</Typography>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: '240px' }}>
                        <InputLabel>Staff</InputLabel>
                        <Select value={staffId} label="Staff" onChange={(e) => setStaffId(e.target.value)}>
                            {staffOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        border: '1px solid', borderColor: 'secondary.border',
                        borderRadius: '6px', padding: '2px 6px'
                    }}>
                        <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronLeftIcon fontSize="small" /></IconButton>
                        <Typography sx={{ fontSize: '13px', minWidth: '110px', textAlign: 'center' }}>
                            {dayjs(`${month}-01`).format('MMMM YYYY')}
                        </Typography>
                        <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronRightIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Box sx={{
                    flex: '1 1 560px', minWidth: '320px', backgroundColor: 'background.light',
                    borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden'
                }}>
                    <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {dayjs(`${month}-01`).format('MMMM YYYY')}
                            {data?.staff_name ? ` · ${data.staff_name}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            {data?.shift_name ? `${data.shift_name} shift` : 'No shift assigned'}
                        </Typography>
                    </Box>

                    {loading ? (
                        <Box sx={{ p: 2 }}>
                            {[0, 1, 2, 3].map(i => <Skeleton key={i} animation="wave" sx={{ height: '52px', background: 'rgb(0 0 0 / 6%)' }} />)}
                        </Box>
                    ) : data ? (
                        <>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', p: 2 }}>
                                {DOW.map(d => (
                                    <Typography key={d} sx={{
                                        fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.06em',
                                        color: 'text.secondary', textAlign: 'center', pb: '4px'
                                    }}>{d}</Typography>
                                ))}
                                {leadingBlanks.map(i => <Box key={`blank-${i}`} />)}
                                {data.days.map(day => {
                                    const style = KIND_STYLE[day.kind] || KIND_STYLE.present;
                                    return (
                                        <Tooltip
                                            key={day.date}
                                            arrow
                                            title={
                                                day.arrival_time
                                                    ? `${day.arrival_time} – ${day.dispersal_time || '—'}`
                                                    : (day.label || 'Nothing recorded')
                                            }
                                        >
                                            <Box sx={{
                                                aspectRatio: '1.35', border: '1px solid', borderColor: style.border,
                                                borderRadius: '6px', padding: '5px 6px', backgroundColor: style.bg,
                                                display: 'flex', flexDirection: 'column', gap: '3px', minHeight: '52px'
                                            }}>
                                                <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>{day.day}</Typography>
                                                <Typography sx={{ fontSize: '9px', fontWeight: 500, lineHeight: 1.25, color: style.color }}>
                                                    {day.label || ''}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '14px', px: 2, pb: 2 }}>
                                {LEGEND.map(item => {
                                    const style = KIND_STYLE[item.kind];
                                    return (
                                        <Box key={item.kind} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <Box sx={{
                                                width: '10px', height: '10px', borderRadius: '2px',
                                                backgroundColor: style.bg, border: '1px solid', borderColor: style.border
                                            }} />
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{item.label}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 700 }}>Pick a staff member to see their month</Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: '1 1 300px', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED', display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Leave balance</Typography>
                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>{dayjs(`${month}-01`).format('YYYY')}</Typography>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Type', 'Entitled', 'Used', 'Left'].map((h, i) => (
                                        <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {balances.map(b => (
                                    <TableRow key={b.leave_category}>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>{b.label}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right' }}>{b.entitled}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right' }}>{b.used}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right', fontWeight: 600 }}>{b.available}</TableCell>
                                    </TableRow>
                                ))}
                                {lop && (
                                    <TableRow>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>Loss of pay</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right' }}>—</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right', color: 'borders.red' }}>{lop.used}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px', textAlign: 'right' }}>—</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>Comp-off credits</Typography>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Earned', 'Expires', 'Status'].map((h, i) => (
                                        <TableCell key={i} sx={{ fontWeight: 600, backgroundColor: 'background.table', fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.comp_off_credits?.length ? data.comp_off_credits.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>{dayjs(c.earned_on).format('DD MMM YYYY')}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>{dayjs(c.expires_on).format('DD MMM YYYY')}</TableCell>
                                        <TableCell sx={{ backgroundColor: 'white', fontSize: '12px' }}>{c.status_label}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow><TableCell colSpan={3} sx={{ backgroundColor: 'white', textAlign: 'center', fontSize: '12px', py: 3 }}>
                                        None earned yet
                                    </TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ backgroundColor: 'background.light', borderRadius: '12px', boxShadow: '0px 0px 25px 0px #00000012', overflow: 'hidden' }}>
                        <Box sx={{ padding: '13px 18px', borderBottom: '1px solid #EDEDED' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>This month</Typography>
                        </Box>
                        <Box sx={{ p: '15px 18px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                            {[
                                ['Present days', data?.summary?.present_days ?? '—'],
                                ['Late marks', data?.summary?.late_marks ?? '—'],
                                ['Leave days', data?.summary?.leave_days ?? '—'],
                                ['Week-offs / holidays', data?.summary?.week_offs ?? '—'],
                                ['Permission used', data?.summary ? `${data.summary.permission_hours} h` : '—'],
                            ].map(([label, value]) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{label}</Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
